import { matchesShortcut } from '@lyrmo/shared/shortcuts';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { Icon } from '../../components/Icon';
import { useStoredWords } from '../../hooks/useStoredWords';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { withViewTransition } from '../../lib/motion';
import { hasFinePointer } from '../../lib/platform';
import { useShortcuts } from '../shortcuts/useShortcuts';
import { FormatBar } from './FormatBar';
import type { Highlight, TextColor } from './colors';
import { FORMAT_KEYS, type FormatKey } from './format-keys';
import { canImport, isImageImport } from './import-file';
import { hasFormatting, lineAt, parseMarkdown } from './markdown';
import {
  breakLine,
  caretPlace,
  clearFormatting,
  copyRange,
  deleteBackward,
  deleteForward,
  editTable,
  formatsAt,
  indent,
  insertDivider,
  insertTable,
  insertText,
  moveCell,
  removeText,
  setBlock,
  setLink,
  setPaint,
  stepForward,
  toggleMark,
  toggleTask,
  wordAt,
  type Block,
  type Edit,
  type Format,
  type Formats,
  type Paint,
  type Pending,
  type TableAction,
} from './markdown-edit';
import { markdownFromHtml, markdownToHtml, plainCopy, safeHref } from './markdown-html';
import {
  drawDraft,
  forget,
  offsetAt,
  readDraft,
  revealCaret,
  selectRange,
  selectionIn,
  type Decorations,
  type DraftField,
} from './rich-field';
import { useEnglishWords } from './useEnglishWords';
import type { WritingAidSettings } from './useWritingAids';
import { completionsAt, typosIn, type Typo } from './word-completion';
import './markdown.css';

export type { DraftField } from './rich-field';

// Once a suggestion has held this long, screen readers are told about it: not at every keystroke.
const ANNOUNCE_AFTER_MS = 600;
// Words the list offers; it scrolls after the first few.
const WORD_LIST_SIZE = 20;
// Words added to the dictionary, which spell check leaves alone.
const DICTIONARY_KEY = 'lyrmo.dictionary';
// The draft, as it's copied between Lyrmo's own fields, formatting and all.
const MARKDOWN_TYPE = 'text/x-lyrmo-markdown';
// The formatting the browser asks for itself, as from a Mac's Format menu or Touch Bar.
const BROWSER_FORMATS = {
  formatBold: 'strong',
  formatItalic: 'em',
  formatUnderline: 'underline',
  formatStrikeThrough: 'strike',
  formatSubscript: 'sub',
  formatSuperscript: 'sup',
} as const satisfies Readonly<Record<string, Format>>;
// Keys that move the caret: the word it was at the end of is written, not being typed any more.
const MOVES = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);
// Deleting more than a character, as far as the browser says: a word, or to a line's end.
const DELETES = new Set([
  'deleteWordBackward',
  'deleteWordForward',
  'deleteSoftLineBackward',
  'deleteSoftLineForward',
  'deleteHardLineBackward',
  'deleteHardLineForward',
  'deleteEntireSoftLine',
  'deleteContent',
  'deleteByCut',
  'deleteByDrag',
]);

/** Part of the text to mark: being worked on, such as by an AI tool, or just done. */
export interface TextMark {
  readonly start: number;
  readonly end: number;
  readonly state: 'working' | 'done';
  /** When it became so, by the page's clock (performance.now()), so its motion keeps time. */
  readonly since?: number | undefined;
  /** Done writing that comes in at its own pace, as an image's: ms from one word to the next. */
  readonly pace?: number | undefined;
}

type Selection = readonly [number, number];

export interface WritingFieldProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onKeyDown' | 'children' | 'contentEditable' | 'role' | 'spellCheck'
> {
  readonly value: string;
  /** `typing` when the change was made in the field: typed, or a suggestion taken. */
  readonly onChange: (value: string, typing?: boolean) => void;
  /** Keys the writing aids and formatting leave alone. */
  readonly onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  readonly fieldRef: RefObject<DraftField | null>;
  /** Which writing aids are on, as `useWritingAids` keeps them. */
  readonly aids: WritingAidSettings;
  /** Text to mark, such as what an AI tool is formatting; it's busy meanwhile. */
  readonly mark?: TextMark | null | undefined;
  readonly placeholder?: string | undefined;
  readonly readOnly?: boolean | undefined;
  /** The formatting toolbar over the draft; on unless said otherwise. */
  readonly formatting?: boolean | undefined;
  /** The browser's own Undo and Redo, as from its Edit menu. */
  readonly onUndo?: (() => void) | undefined;
  readonly onRedo?: (() => void) | undefined;
  /** Files pasted or dropped, for the editor to read into the draft, in place of `at`. */
  readonly onFiles?: ((files: readonly File[], at: Selection) => void) | undefined;
  /**
   * Writing and files pasted or dropped anywhere on the page come into the draft, unless another
   * field, a menu, or a dialog takes them; and typing, with nothing else focused, starts in it.
   */
  readonly pageWide?: boolean | undefined;
}

/** Where the selection is, kept while the field doesn't have it, as a textarea keeps its own. */
interface Model {
  /** The text the field holds: the value, or an edit of it on its way to being the value. */
  value: string;
  selection: readonly [number, number, 'forward' | 'backward' | 'none'];
  /** The selection to put in place once an edit is drawn. */
  wanted: { readonly text: string; readonly selection: Selection } | null;
  /** The text last drawn, to tell an edit made elsewhere from one made here. */
  drawn: string | null;
  /** Text set from outside, as a test or a script sets a textarea's value. */
  set: string | null;
  /** What's being dragged from the field, to move it. */
  dragged: Selection | null;
}

/**
 * The draft's field: the text, formatted, as in Jira or Docs, with a toolbar for formatting it,
 * and the writing aids: the rest of the English word being typed, in grey after the caret, and
 * with the word list on, the words that start with the letters typed, or that they could be a
 * typo for, to pick from; and spell check, which underlines words that look mistyped. The text
 * is Markdown (markdown.ts), so it's the draft as it's written that's sent, undone, and kept.
 */
export function WritingField({
  value,
  onChange,
  onKeyDown,
  fieldRef,
  aids,
  mark = null,
  className,
  readOnly = false,
  placeholder,
  formatting = true,
  onUndo,
  onRedo,
  onFiles,
  pageWide = false,
  id: fieldId,
  ...props
}: WritingFieldProps) {
  const { t, locale } = useI18n();
  const { platform } = useShortcuts();
  const id = useId();
  const { completeWords, showList, spellCheck } = aids;

  const elementRef = useRef<DraftField | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const model = useRef<Model>({
    value,
    selection: [value.length, value.length, 'none'],
    wanted: null,
    drawn: null,
    set: null,
    dragged: null,
  });
  // An input method, as for Japanese or a phone's keyboard, is putting a word together.
  const composingRef = useRef(false);
  const pointerFocus = useRef(false);
  const [caret, setCaret] = useState<number | null>(null);
  const [range, setRange] = useState<Selection>([value.length, value.length]);
  const [composing, setComposing] = useState(false);
  // Formatting for the next words typed, as after Bold with nothing selected, and where.
  const [pending, setPending] = useState<{ readonly formats: Pending; readonly at: number } | null>(
    null,
  );
  const [linkRequest, setLinkRequest] = useState(0);
  // Writing or files from another app, dragged over the page, which the draft is ready to take:
  // over the draft itself, or somewhere else on the page.
  const [dropping, setDropping] = useState<'draft' | 'page' | null>(null);
  // Asks for the field to be drawn again, as after the browser changed it itself.
  const [, redraw] = useState(0);
  // Touch screens have no Tab key, and their keyboards suggest words of their own.
  const [completes] = useState(hasFinePointer);
  // Where a suggestion was just used or turned down: none there until the person types on.
  const [settled, setSettled] = useState<{ value: string; caret: number } | null>(null);
  // The word picked in the list with the arrow keys, and the letters it was picked for.
  const [picked, setPicked] = useState<{ letters: string; index: number } | null>(null);
  // Words are completed, and checked, while the text reads as English, whatever the versions are
  // to be written in; before there's a word to tell by, if the site is in English.
  const english = useEnglishWords(value, locale === 'en');
  const completions = useMemo(
    () =>
      english && caret !== null && !composing && (completeWords || showList)
        ? completionsAt(english, value, caret, showList ? WORD_LIST_SIZE : 1)
        : null,
    [caret, completeWords, composing, english, showList, value],
  );

  // Spell check: words that look mistyped are underlined, and a click on one lists the words it
  // was likely meant to be. It stands in for the browser's while the text reads as English.
  const [dictionary, addToDictionary] = useStoredWords(DICTIONARY_KEY);
  // Where typing left the caret: the word just before it is still being written.
  const [typedTo, setTypedTo] = useState<number | null>(null);
  // The typo whose suggestions were opened, in the draft as it was then, and the one picked.
  const [spelling, setSpelling] = useState<{
    value: string;
    start: number;
    picked: number | null;
  } | null>(null);
  const checking = spellCheck && english !== null && completes;
  // Code, and where links go, are left alone.
  const code = useMemo((): Selection[] => {
    if (!hasFormatting(value)) return [];
    return parseMarkdown(value).flatMap((line): Selection[] =>
      line.kind === 'code' || line.kind === 'fence'
        ? [[line.start, line.end]]
        : line.spans
            .filter((span) => span.kind === 'code' || span.kind === 'link')
            .map((span): Selection =>
              span.kind === 'code' ? [span.start, span.end] : [span.to, span.end],
            ),
    );
  }, [value]);
  const found = useMemo(
    () =>
      spellCheck && english && completes
        ? typosIn(english, value).filter(
            ({ start, end }) => !code.some(([from, to]) => start < to && end > from),
          )
        : [],
    [code, completes, english, spellCheck, value],
  );
  // Less the words in the dictionary, and the word being typed until it's written or asked about.
  const mistyped = found.filter(
    ({ start, end }) => !dictionary.has(value.slice(start, end).toLowerCase()),
  );
  const typos = mistyped.filter(({ end }) => !(end === caret && caret === typedTo));
  // The suggestions stay open while the draft is unchanged and the caret is in the word.
  const fixing =
    spelling?.value === value && caret !== null
      ? typos.find(({ start, end }) => start === spelling.start && start <= caret && caret <= end)
      : undefined;
  const fixPicked = fixing ? (spelling?.picked ?? null) : null;
  // What Tab takes: the suggestion picked, or else the likeliest.
  const fixIndex = fixPicked ?? 0;

  const open =
    !fixing && completions && !(settled?.value === value && settled.caret === completions.end)
      ? completions
      : null;
  const list = showList ? open : null;
  const letters = open ? `${String(open.start)}:${value.slice(open.start, open.end)}` : '';
  const pickedIndex = list && picked?.letters === letters ? picked.index : null;
  // What Tab puts in: the word picked, or else the likely one.
  const active = pickedIndex ?? (open?.likely ? 0 : null);
  const chosen = open && active !== null ? open.words[active] : undefined;
  // The grey text: the rest of the word. A typo is underlined instead, once it's written.
  const rest =
    open && !open.typo && chosen !== undefined && completes && completeWords
      ? chosen.slice(open.end - open.start)
      : '';
  // Screen readers hear the list through its active option, and the grey word from a live region.
  const suggested = !list && rest ? (chosen ?? '') : '';
  const [announced, setAnnounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(
      () => {
        setAnnounced(suggested);
      },
      suggested ? ANNOUNCE_AFTER_MS : 0,
    );
    return () => {
      clearTimeout(timer);
    };
  }, [suggested]);

  const decorations: Decorations = {
    typos: typos.map((typo) => ({ start: typo.start, end: typo.end, open: typo === fixing })),
    mark,
    completion: open && (rest || list) ? { start: open.start, end: open.end, rest } : null,
  };
  const formats = useMemo(
    () =>
      formatsAt(
        value,
        range,
        pending?.at === range[0] && range[0] === range[1] ? pending.formats : {},
      ),
    [pending, range, value],
  );

  /** The selection now: the field's own while it has one, or else where it was last. */
  const currentSelection = useCallback((): Model['selection'] => {
    const element = elementRef.current;
    const { wanted, selection } = model.current;
    if (wanted) return [wanted.selection[0], wanted.selection[1], 'forward'];
    const live = element && !composingRef.current ? selectionIn(element) : null;
    if (!live) return selection;
    const [start, end, direction] = live;
    if (start !== end) return [start, end, direction];
    const at = caretPlace(model.current.value, start);
    return [at, at, 'none'];
  }, []);

  /** Notes where the selection is, for the writing aids and the toolbar. */
  const track = useCallback((start: number, end: number) => {
    setRange((now) => (now[0] === start && now[1] === end ? now : [start, end]));
    setCaret(start === end && document.activeElement === elementRef.current ? end : null);
  }, []);

  /** Puts an edit in the draft, and once it's drawn, its selection. */
  function commit(edit: Edit, typing: boolean, typed = typing) {
    const element = elementRef.current;
    // A caret goes where it can be seen: not on a code block's fence, say, once a paste ends there.
    const [start, end] =
      edit.selection[0] === edit.selection[1]
        ? [caretPlace(edit.text, edit.selection[0]), caretPlace(edit.text, edit.selection[0])]
        : edit.selection;
    model.current.selection = [start, end, 'forward'];
    track(start, end);
    setTypedTo(typed ? end : null);
    if (edit.text === model.current.value) {
      model.current.wanted = null;
      if (element && document.activeElement === element) {
        selectRange(element, start, end);
        revealCaret(element);
      }
      return;
    }
    model.current.value = edit.text;
    model.current.wanted = { text: edit.text, selection: [start, end] };
    onChange(edit.text, typing);
  }

  /** An edit from the toolbar or a formatting key, then back to typing. */
  const apply = (edit: Edit | null) => {
    const element = elementRef.current;
    if (!edit || readOnly || !element) return;
    setPending(null);
    commit(edit, false);
    if (document.activeElement !== element) element.focus({ preventScroll: true });
  };
  const selected = (): Selection => {
    const [start, end] = currentSelection();
    return [start, end];
  };

  function format(kind: Format) {
    if (readOnly) return;
    const selection = selected();
    const edit = toggleMark(value, selection, kind);
    if (edit) {
      apply(edit);
      return;
    }
    // Nothing to format: the next words typed are, or aren't. They're raised or lowered, not both.
    const [at] = selection;
    const now = pending?.at === at ? pending.formats : {};
    const on = formatsAt(value, [at, at], now).marks.has(kind);
    const other = kind === 'sub' ? 'sup' : kind === 'sup' ? 'sub' : null;
    setPending({ formats: { ...now, [kind]: !on, ...(other && !on && { [other]: false }) }, at });
    const element = elementRef.current;
    if (element && document.activeElement !== element) {
      element.focus({ preventScroll: true });
      selectRange(element, at, at);
    }
  }
  /** Colors or highlights the selection; with nothing to color, the next words typed instead. */
  function paint(kind: Paint, color: TextColor | Highlight | null) {
    if (readOnly) return;
    const selection = selected();
    const edit = setPaint(value, selection, kind, color);
    if (edit) {
      apply(edit);
      return;
    }
    const [at] = selection;
    const now = pending?.at === at ? pending.formats : {};
    setPending({ formats: { ...now, [kind]: color }, at });
    const element = elementRef.current;
    if (element && document.activeElement !== element) {
      element.focus({ preventScroll: true });
      selectRange(element, at, at);
    }
  }
  function block(kind: Block) {
    if (!readOnly) apply(setBlock(value, selected(), kind));
  }
  function table(action: TableAction | 'insert') {
    if (readOnly) return;
    apply(
      action === 'insert' ? insertTable(value, selected()) : editTable(value, selected(), action),
    );
  }
  function clear() {
    apply(clearFormatting(value, selected()));
  }
  function formatKey(name: FormatKey) {
    if (name === 'link') {
      if (formatting && !readOnly) setLinkRequest((count) => count + 1);
    } else if (name === 'bullet' || name === 'ordered' || name === 'task') block(name);
    else if (name === 'clear') clear();
    else format(name);
  }

  // The field takes a textarea's value and selection, for the editor around it.
  const attach = useCallback(
    (element: HTMLDivElement | null) => {
      const field = element as DraftField | null;
      elementRef.current = field;
      fieldRef.current = field;
      if (!field || Object.hasOwn(field, 'selectionStart')) return;
      const setSelection = (
        start: number,
        end: number,
        direction: 'forward' | 'backward' | 'none' = 'none',
      ) => {
        const length = model.current.value.length;
        const from = Math.max(0, Math.min(start, length));
        const to = Math.max(from, Math.min(end, length));
        model.current.selection = [from, to, direction];
        if (model.current.wanted)
          model.current.wanted = { ...model.current.wanted, selection: [from, to] };
        if (document.activeElement === field && !composingRef.current) {
          selectRange(field, from, to, direction);
          revealCaret(field);
        }
        track(from, to);
      };
      Object.defineProperties(field, {
        value: {
          configurable: true,
          get: () => model.current.value,
          set: (text: string) => {
            model.current.value = text;
            model.current.set = text;
            model.current.selection = [text.length, text.length, 'none'];
            drawDraft(field, text, { typos: [], mark: null, completion: null });
          },
        },
        readOnly: { configurable: true, get: () => field.getAttribute('aria-readonly') === 'true' },
        selectionStart: { configurable: true, get: () => currentSelection()[0] },
        selectionEnd: { configurable: true, get: () => currentSelection()[1] },
        selectionDirection: { configurable: true, get: () => currentSelection()[2] },
        setSelectionRange: { configurable: true, value: setSelection },
        select: {
          configurable: true,
          value: () => {
            setSelection(0, model.current.value.length);
          },
        },
      });
    },
    [currentSelection, fieldRef, track],
  );

  // Draws the draft, and puts the selection where an edit left it. While an input method is at
  // work, the field is left as it is: the word it's making is in it, and drawing would undo it.
  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element || composingRef.current) return;
    const changed = drawDraft(element, value, decorations);
    const focused = document.activeElement === element;
    const { wanted, drawn } = model.current;
    model.current.drawn = value;
    if (model.current.value !== value && model.current.set === null) model.current.value = value;
    // An edit the editor turned down, as while it's locked, leaves the draft as it was.
    if (wanted && wanted.text !== value) model.current.wanted = null;
    if (wanted?.text === value) {
      model.current.wanted = null;
      if (focused) {
        selectRange(element, wanted.selection[0], wanted.selection[1]);
        revealCaret(element);
      }
    } else if (drawn !== null && drawn !== value) {
      // Changed from outside, as by Undo: the caret goes to the end, as in a textarea, or the end
      // of what can be seen, before a code block's fence.
      const end = caretPlace(value, value.length);
      model.current.wanted = null;
      model.current.selection = [end, end, 'none'];
      if (focused) selectRange(element, end, end);
      track(end, end);
    } else if (changed && focused) {
      const [start, end, direction] = model.current.selection;
      selectRange(element, start, end, direction);
    }
    placeList();
  });

  // The browser's own editing, mapped onto the draft's: what's typed, deleted, and pasted goes
  // through the edits in markdown-edit.ts, so formatting stays whole.
  const beforeInput = useEffectEvent((event: InputEvent) => {
    const element = elementRef.current;
    if (!element || composingRef.current || event.isComposing) return;
    const type = event.inputType;
    if (
      type === 'insertCompositionText' ||
      type === 'insertFromComposition' ||
      type === 'deleteCompositionText'
    )
      return;
    if (readOnly) {
      if (event.cancelable) event.preventDefault();
      return;
    }
    if (!event.cancelable) return;
    const [start, end] = selected();
    const target = targetRange(element, event);
    const text = model.current.value;
    let edit: Edit;
    // Formatting for the words typed next, carried on after the edit.
    let carry: Pending | null = null;
    switch (type) {
      case 'insertText':
      case 'insertReplacementText': {
        const input = (event.data ?? event.dataTransfer?.getData('text/plain') ?? '').replace(
          /\r\n?/gu,
          '\n',
        );
        const over = type === 'insertReplacementText' && target ? target : ([start, end] as const);
        const formats = pending?.at === over[0] && over[0] === over[1] ? pending.formats : {};
        edit = input.includes('\n')
          ? insertText(text, over, input)
          : insertText(text, over, input, formats);
        setPending(null);
        break;
      }
      case 'insertParagraph':
      case 'insertLineBreak': {
        edit = breakLine(text, [start, end], type === 'insertLineBreak');
        // From the end of bold words, say, the new line is written in bold, as in Docs.
        carry = carriedOn(
          formatsAt(text, [start, start], pending?.at === start ? pending.formats : {}),
          edit,
        );
        break;
      }
      case 'deleteContentBackward':
        edit = start === end ? deleteBackward(text, start) : removeText(text, [start, end]);
        break;
      case 'deleteContentForward':
        edit = start === end ? deleteForward(text, start) : removeText(text, [start, end]);
        break;
      case 'historyUndo':
        event.preventDefault();
        onUndo?.();
        return;
      case 'historyRedo':
        event.preventDefault();
        onRedo?.();
        return;
      case 'formatBold':
      case 'formatItalic':
      case 'formatUnderline':
      case 'formatStrikeThrough':
      case 'formatSubscript':
      case 'formatSuperscript': {
        event.preventDefault();
        format(BROWSER_FORMATS[type]);
        return;
      }
      default:
        if (type.startsWith('format') || type.startsWith('insertFrom')) {
          // Pastes and drops are taken care of by their own events; other formatting, such as
          // a font or a color, isn't the draft's.
          event.preventDefault();
          return;
        }
        if (DELETES.has(type)) {
          const over = target ?? wordRange(text, start, end, type);
          edit = removeText(text, over);
          break;
        }
        // Anything else the browser does itself, and the field reads what it did.
        return;
    }
    event.preventDefault();
    setSpelling(null);
    commit(edit, true);
    if (carry) setPending({ formats: carry, at: caretPlace(edit.text, edit.selection[0]) });
  });

  /** What the browser changed itself, as an input method does, read back into the draft. */
  const readBack = useEffectEvent(() => {
    const element = elementRef.current;
    if (!element || composingRef.current) return;
    if (model.current.set !== null) {
      const text = model.current.set;
      model.current.set = null;
      model.current.drawn = text;
      onChange(text, true);
      return;
    }
    const text = readDraft(element);
    const live = selectionIn(element);
    forget(element);
    if (text === model.current.value || readOnly) {
      redraw((count) => count + 1);
      return;
    }
    const caret = live?.[1] ?? text.length;
    commit({ text, selection: [live?.[0] ?? caret, caret] }, true);
    redraw((count) => count + 1);
  });

  const transfer = useEffectEvent((data: DataTransfer | null, cut: boolean) => {
    const [start, end] = selected();
    if (!data || start === end) return false;
    const text = model.current.value;
    const [from, to] = copyRange(text, [start, end]);
    const copied = text.slice(from, to);
    data.setData('text/plain', plainCopy(copied));
    if (hasFormatting(copied)) {
      data.setData('text/html', markdownToHtml(copied));
      data.setData(MARKDOWN_TYPE, copied);
    }
    if (cut && !readOnly) commit(removeText(text, [start, end]), true);
    return true;
  });

  /** What's pasted or dropped: Lyrmo's own formatting, or another app's, or plain text. */
  const pasted = (data: DataTransfer | null, at: Selection): string => {
    if (!data) return '';
    const line = lineAt(parseMarkdown(model.current.value), at[0]);
    const plain = data.getData('text/plain').replace(/\r\n?/gu, '\n');
    // In code, writing is code, as it is.
    if (line.kind === 'code' || line.kind === 'fence') return plain;
    const own = data.getData(MARKDOWN_TYPE);
    return own ? own : (markdownFromHtml(data.getData('text/html')) ?? plain);
  };

  /** The files pasted or dropped, when it's files that come rather than writing. */
  const filesIn = (data: DataTransfer | null): readonly File[] => {
    const files = onFiles ? Array.from(data?.files ?? []) : [];
    // A file copied in Finder or Explorer comes with its name as text, but it's the file that's
    // wanted. Word copies a picture of the writing along with it, but it's the writing.
    const writingFile = files.some((file) => canImport(file) && !isImageImport(file));
    const plain = data?.getData('text/plain').trim() ?? '';
    const fileNames = files.map((file) => file.name).join('\n');
    // A copied image file may carry its filename as plain text. Rich clipboard writing takes
    // precedence over its preview image, so pasting from Word doesn't run text recognition.
    const imageFile =
      files.some(isImageImport) &&
      (!plain || plain === fileNames) &&
      !markdownFromHtml(data?.getData('text/html') ?? '')?.trim();
    return writingFile || imageFile || !carriesText(data) ? files : [];
  };

  const paste = useEffectEvent((event: ClipboardEvent) => {
    event.preventDefault();
    if (readOnly) return;
    const selection = selected();
    const files = filesIn(event.clipboardData);
    if (files.length > 0) {
      onFiles?.(files, selection);
      return;
    }
    const input = pasted(event.clipboardData, selection);
    if (input) commit(insertText(model.current.value, selection, input), true);
  });

  const drop = useEffectEvent((event: DragEvent) => {
    const element = elementRef.current;
    const dragged = model.current.dragged;
    model.current.dragged = null;
    const files = dragged ? [] : filesIn(event.dataTransfer);
    if (!element || (files.length === 0 && !carriesText(event.dataTransfer))) return;
    event.preventDefault();
    if (readOnly) return;
    const point = pointFrom(element, event.clientX, event.clientY);
    const text = model.current.value;
    const at = point ?? selected()[0];
    if (dragged) {
      // Moved within the draft: out of where it was, into where it's dropped.
      if (at >= dragged[0] && at <= dragged[1]) return;
      const moving = text.slice(dragged[0], dragged[1]);
      const removed = removeText(text, dragged);
      const place = at > dragged[1] ? at - (text.length - removed.text.length) : at;
      const edit = insertText(removed.text, [place, place], moving);
      commit({ text: edit.text, selection: [place, edit.selection[1]] }, false);
      return;
    }
    // From elsewhere, it goes in where it's dropped, with the caret after it, to write on.
    if (files.length > 0) {
      onFiles?.(files, [at, at]);
      return;
    }
    const input = pasted(event.dataTransfer, [at, at]);
    if (!input) return;
    commit(insertText(text, [at, at], input), false);
    element.focus({ preventScroll: true });
  });

  // Files dragged over the draft are taken, rather than opened by the browser in place of the
  // page. Writing finds its place in the draft as the browser shows it.
  const dragOver = useEffectEvent((event: DragEvent) => {
    if (!onFiles || model.current.dragged || !event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = readOnly ? 'none' : 'copy';
  });

  /**
   * Writing or files pasted or dropped elsewhere on the page, put in the draft: where the caret
   * was in it, or after what was selected there, which stays. Then it's the draft that's focused,
   * with the caret after them, to write on, as if they'd been pasted there.
   */
  const bring = (data: DataTransfer | null) => {
    const element = elementRef.current;
    if (!element) return;
    const [, end] = selected();
    const files = filesIn(data);
    if (files.length > 0) {
      onFiles?.(files, [end, end]);
      return;
    }
    const input = pasted(data, [end, end]);
    if (!input) return;
    const edit = insertText(model.current.value, [end, end], input);
    withViewTransition(
      'paste',
      () => {
        commit(edit, false);
      },
      {
        after: () => {
          const [start, end] = model.current.selection;
          element.focus();
          element.setSelectionRange(start, end);
        },
      },
    );
  };

  const pagePaste = useEffectEvent((event: ClipboardEvent) => {
    const element = elementRef.current;
    if (!element || event.defaultPrevented || !fromPage(element, event.target)) return;
    event.preventDefault();
    if (!readOnly) bring(event.clipboardData);
  });

  // What's being dragged over the page from another app has gone into, to tell when it's left;
  // and whether a drag is from the page itself, as of words in a version, which only the draft
  // takes, where it's dropped on it.
  const drag = useRef({ over: new Set<EventTarget>(), inside: false });

  const pageDrag = useEffectEvent((event: DragEvent) => {
    const element = elementRef.current;
    const { over } = drag.current;
    if (event.type === 'dragstart') drag.current.inside = true;
    if (event.type === 'dragend' || event.type === 'drop') {
      drag.current.inside = false;
      over.clear();
    }
    const outside =
      !drag.current.inside &&
      (event.dataTransfer?.types.includes('Files') === true || carriesText(event.dataTransfer));
    if (event.type === 'dragenter' && outside && event.target) over.add(event.target);
    if (event.type === 'dragleave' && event.target) over.delete(event.target);
    // Some browsers say nothing of leaving what's been taken away meanwhile.
    for (const target of over)
      if (target instanceof Node && !target.isConnected) over.delete(target);
    if (over.size === 0) setDropping(null);
    else if (event.type !== 'dragleave')
      setDropping(
        element && event.target instanceof Node && element.contains(event.target)
          ? 'draft'
          : 'page',
      );
    if (!element || !outside || event.defaultPrevented || !fromPage(element, event.target)) return;
    if (event.type === 'dragover' || event.type === 'dragenter') {
      // Taken, rather than opened by the browser in place of the page.
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = readOnly ? 'none' : 'copy';
    } else if (event.type === 'drop') {
      event.preventDefault();
      if (!readOnly) bring(event.dataTransfer);
    }
  });

  // A key typed with nothing focused, as after a click on the page, starts writing in the draft.
  const pageKey = useEffectEvent((event: globalThis.KeyboardEvent) => {
    const element = elementRef.current;
    const focused = document.activeElement;
    if (
      !element ||
      readOnly ||
      event.defaultPrevented ||
      event.isComposing ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      // A character, but not a space, which scrolls the page.
      !/^.$/su.test(event.key) ||
      event.key === ' ' ||
      (focused !== null && focused !== document.body) ||
      document.querySelector('dialog[open]')
    )
      return;
    // The key goes on to the draft, which has the caret now: where it was, or after what was
    // selected, which isn't to be typed over unseen.
    const [, end] = selected();
    element.focus();
    element.setSelectionRange(end, end);
  });

  useEffect(() => {
    if (!pageWide) return;
    const onPaste = (event: ClipboardEvent) => {
      pagePaste(event);
    };
    const onDrag = (event: DragEvent) => {
      pageDrag(event);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      pageKey(event);
    };
    const drags = ['dragstart', 'dragend', 'dragenter', 'dragleave', 'dragover', 'drop'] as const;
    const { over } = drag.current;
    document.addEventListener('paste', onPaste);
    for (const type of drags) document.addEventListener(type, onDrag);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('paste', onPaste);
      for (const type of drags) document.removeEventListener(type, onDrag);
      document.removeEventListener('keydown', onKeyDown);
      over.clear();
      setDropping(null);
    };
  }, [pageWide]);

  const dragStart = useEffectEvent((event: DragEvent) => {
    const [start, end] = selected();
    if (start === end || !transfer(event.dataTransfer, false)) return;
    model.current.dragged = copyRange(model.current.value, [start, end]);
  });

  const selectionChange = useEffectEvent(() => {
    const element = elementRef.current;
    if (!element || composingRef.current || model.current.wanted) return;
    const live = selectionIn(element);
    if (!live) return;
    const [start, end, direction] = live;
    const at = start === end ? caretPlace(model.current.value, start) : null;
    model.current.selection = at === null ? [start, end, direction] : [at, at, 'none'];
    track(at ?? start, at ?? end);
    if (pending && (at === null || at !== pending.at)) setPending(null);
  });

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const onBeforeInput = (event: InputEvent) => {
      beforeInput(event);
    };
    const onInput = () => {
      readBack();
    };
    const onCompositionStart = () => {
      composingRef.current = true;
      setComposing(true);
    };
    const onCompositionEnd = () => {
      composingRef.current = false;
      setComposing(false);
      readBack();
    };
    const onCopy = (event: ClipboardEvent) => {
      if (transfer(event.clipboardData, false)) event.preventDefault();
    };
    const onCut = (event: ClipboardEvent) => {
      if (transfer(event.clipboardData, true)) event.preventDefault();
    };
    const onPaste = (event: ClipboardEvent) => {
      paste(event);
    };
    const onDragStart = (event: DragEvent) => {
      dragStart(event);
    };
    const onDragEnd = () => {
      model.current.dragged = null;
    };
    const onDrop = (event: DragEvent) => {
      drop(event);
    };
    const onDragOver = (event: DragEvent) => {
      dragOver(event);
    };
    const onSelectionChange = () => {
      selectionChange();
    };
    element.addEventListener('beforeinput', onBeforeInput);
    element.addEventListener('input', onInput);
    element.addEventListener('change', onInput);
    element.addEventListener('compositionstart', onCompositionStart);
    element.addEventListener('compositionend', onCompositionEnd);
    element.addEventListener('copy', onCopy);
    element.addEventListener('cut', onCut);
    element.addEventListener('paste', onPaste);
    element.addEventListener('dragstart', onDragStart);
    element.addEventListener('dragend', onDragEnd);
    element.addEventListener('drop', onDrop);
    element.addEventListener('dragenter', onDragOver);
    element.addEventListener('dragover', onDragOver);
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      element.removeEventListener('beforeinput', onBeforeInput);
      element.removeEventListener('input', onInput);
      element.removeEventListener('change', onInput);
      element.removeEventListener('compositionstart', onCompositionStart);
      element.removeEventListener('compositionend', onCompositionEnd);
      element.removeEventListener('copy', onCopy);
      element.removeEventListener('cut', onCut);
      element.removeEventListener('paste', onPaste);
      element.removeEventListener('dragstart', onDragStart);
      element.removeEventListener('dragend', onDragEnd);
      element.removeEventListener('drop', onDrop);
      element.removeEventListener('dragenter', onDragOver);
      element.removeEventListener('dragover', onDragOver);
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, []);

  // And whenever the field changes size without anything rendering, as when its window does:
  // its lines wrap anew, and the list must follow its word.
  const refit = useEffectEvent(() => {
    placeList();
  });
  useEffect(() => {
    const element = elementRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      refit();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  /** Puts the open list under the word it's for, and keeps its active word in view. */
  function placeList() {
    const element = elementRef.current;
    const listbox = listRef.current;
    const container = element?.parentElement;
    // A typo whose fixes are open, or else the letters being typed.
    const word =
      element?.querySelector<HTMLElement>('.editor-typo.is-open') ??
      element?.querySelector<HTMLElement>('.editor-ghost-typed');
    if (!listbox || !word || !container) return;
    const box = container.getBoundingClientRect();
    const at = word.getBoundingClientRect();
    const left = Math.min(at.left - box.left, container.clientWidth - listbox.offsetWidth);
    listbox.style.left = `${String(Math.max(0, left))}px`;
    listbox.style.top = `${String(at.bottom - box.top + 4)}px`;
    const item = listbox.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!item) return;
    if (item.offsetTop < listbox.scrollTop) listbox.scrollTop = item.offsetTop;
    const below = item.offsetTop + item.offsetHeight - listbox.clientHeight;
    if (below > listbox.scrollTop) listbox.scrollTop = below;
  }

  /** Puts `word` in place of the draft from `start` to `end`, with the caret after it. */
  function replace(start: number, end: number, word: string) {
    const at = start + word.length;
    const next = value.slice(0, start) + word + value.slice(end);
    commit({ text: next, selection: [at, at] }, true, false);
    // The word is whole now: suggest again once the person types on.
    setSettled({ value: next, caret: at });
  }

  /** Puts `word` in place of the letters typed, which start it or are a typo of it. */
  function complete(word: string) {
    if (open) replace(open.start, open.end, word);
  }

  /**
   * Opens the suggestions for the typo the selection is in, with the one at `picked` picked, or
   * closes them if it isn't in one.
   */
  function openSpelling(picked: number | null): Typo | undefined {
    const [from, to] = selected();
    const typo = mistyped.find(({ start, end }) => start <= from && to <= end);
    if (typo) setTypedTo(null);
    setSpelling(typo ? { value, start: typo.start, picked } : null);
    return typo;
  }

  /**
   * Puts the suggestion at `index` in place of the typo, or for the choice after them, adds the
   * typo to the dictionary.
   */
  function takeFix(index: number) {
    if (!fixing) return;
    const fix = fixing.fixes[index];
    if (fix === undefined) addToDictionary(value.slice(fixing.start, fixing.end).toLowerCase());
    else replace(fixing.start, fixing.end, fix);
    setSpelling(null);
  }

  /** Moves the active word in the list by `step`, wrapping around at either end. */
  function pick(step: 1 | -1) {
    if (!list) return;
    const count = list.words.length;
    const from = active ?? (step > 0 ? -1 : 0);
    setPicked({ letters, index: (from + step + count) % count });
  }

  return (
    <>
      {formatting && (
        <FormatBar
          fieldId={fieldId}
          formats={formats}
          disabled={readOnly}
          onMark={format}
          onBlock={block}
          onLink={(href) => {
            apply(setLink(value, selected(), href));
          }}
          onClear={clear}
          onDivider={() => {
            apply(insertDivider(value, selected()));
          }}
          onPaint={paint}
          onTable={table}
          onReturn={() => {
            elementRef.current?.focus({ preventScroll: true });
          }}
          linkRequest={linkRequest}
        />
      )}
      <div className={cx('editor-field', mark?.state === 'working' && 'is-working')}>
        <div
          {...props}
          ref={attach}
          id={fieldId}
          className={cx(className, 'rich-text', !value && 'is-empty')}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-readonly={readOnly || undefined}
          aria-placeholder={placeholder}
          data-placeholder={placeholder}
          translate="no"
          aria-busy={mark?.state === 'working' || undefined}
          // Turned off, spell check is off altogether, the browser's too.
          spellCheck={spellCheck && !checking}
          aria-autocomplete={showList ? 'list' : undefined}
          aria-controls={list ? `${id}-words` : fixing ? `${id}-fixes` : undefined}
          aria-activedescendant={
            list && active !== null
              ? `${id}-word-${String(active)}`
              : fixing
                ? `${id}-fix-${String(fixIndex)}`
                : undefined
          }
          onKeyDown={(event) => {
            if (readOnly) return;
            const plain =
              !event.altKey && !event.ctrlKey && !event.metaKey && !event.nativeEvent.isComposing;
            if (fixing && plain) {
              const count = fixing.fixes.length + 1;
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                const index = (fixIndex + step + count) % count;
                setSpelling({ value, start: fixing.start, picked: index });
                return;
              }
              // As in the word list, Enter only takes a suggestion picked with the arrow keys.
              const takes =
                (event.key === 'Tab' && !event.shiftKey) ||
                (event.key === 'Enter' && !event.shiftKey && fixPicked !== null);
              if (takes) {
                event.preventDefault();
                takeFix(fixIndex);
                return;
              }
              if (event.key === 'Escape') {
                // Just the suggestions go: Escape otherwise leaves full screen.
                event.preventDefault();
                event.stopPropagation();
                setSpelling(null);
                return;
              }
            }
            if (open && plain && (list || rest)) {
              if (list && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                event.preventDefault();
                pick(event.key === 'ArrowDown' ? 1 : -1);
                return;
              }
              // Enter only takes a word picked with the arrow keys; otherwise it starts a line.
              const takes =
                (event.key === 'Tab' && !event.shiftKey) ||
                (event.key === 'Enter' && !event.shiftKey && pickedIndex !== null);
              if (takes && chosen !== undefined) {
                event.preventDefault();
                complete(chosen);
                return;
              }
              if (event.key === 'Escape') {
                // Just the suggestions go: Escape otherwise leaves full screen.
                event.preventDefault();
                event.stopPropagation();
                setSettled({ value, caret: open.end });
                return;
              }
              // The rest of the word is drawn after the caret, but isn't written: the caret
              // steps over the next character of the draft, not over it.
              if (rest && event.key === 'ArrowRight' && !event.shiftKey && caret === open.end) {
                event.preventDefault();
                const next = stepForward(value, caret);
                elementRef.current?.setSelectionRange(next, next);
                return;
              }
            }
            if (MOVES.has(event.key)) setTypedTo(null);
            const shortcut = (Object.keys(FORMAT_KEYS) as FormatKey[]).find((name) =>
              matchesShortcut(event, FORMAT_KEYS[name], platform),
            );
            if (shortcut) {
              event.preventDefault();
              formatKey(shortcut);
              return;
            }
            if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
              // In a table, the next cell; in a list, a level in or out.
              const edit =
                moveCell(value, selected(), event.shiftKey) ??
                indent(value, selected(), event.shiftKey);
              if (edit) {
                event.preventDefault();
                apply(edit);
                return;
              }
            }
            onKeyDown?.(event);
          }}
          onPointerDown={(event) => {
            setTypedTo(null);
            pointerFocus.current = true;
            const target = event.target as HTMLElement;
            // A checklist item's box ticks it, leaving the caret where it was.
            if (target.classList.contains('md-check') && !readOnly) {
              event.preventDefault();
              const line = target.closest('.md-line');
              const element = elementRef.current;
              if (!line || !element) return;
              const at = offsetAt(element, line, 0);
              const edit = toggleTask(value, at);
              if (edit) {
                const [start, end] = currentSelection();
                commit({ text: edit.text, selection: [start, end] }, false);
              }
            }
          }}
          onClick={(event) => {
            // ⌘ or Ctrl and a click on a link opens it, as in other editors.
            const link = (event.target as HTMLElement).closest<HTMLElement>('.md-link');
            const href = safeHref(link?.title);
            if (href && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              window.open(href, '_blank', 'noopener,noreferrer');
              return;
            }
            // A click on a typo opens its suggestions; a drag or a double click selects instead.
            const [start, end] = selected();
            if (start === end) openSpelling(null);
            else setSpelling(null);
          }}
          onContextMenu={(event) => {
            // On a typo, its suggestions stand in for the browser's menu, from the keyboard too.
            const typo = openSpelling(0);
            if (!typo) return;
            event.preventDefault();
            const [start, end] = selected();
            if (start === end) return;
            // Some browsers select the word first: the caret goes to its end instead.
            elementRef.current?.setSelectionRange(typo.end, typo.end);
          }}
          onFocus={() => {
            const element = elementRef.current;
            // Back by the keyboard, or from the toolbar, the selection is where it was.
            if (element && !pointerFocus.current) {
              const [start, end, direction] = model.current.selection;
              selectRange(element, start, end, direction);
              track(start, end);
            }
            pointerFocus.current = false;
          }}
          onBlur={() => {
            setCaret(null);
            setTypedTo(null);
            pointerFocus.current = false;
          }}
          onScroll={placeList}
        />
        {list && (
          <ul
            ref={listRef}
            id={`${id}-words`}
            className="editor-word-list"
            role="listbox"
            aria-label={t.editor.wordList}
          >
            {list.words.map((word, index) => (
              <li
                key={word}
                id={`${id}-word-${String(index)}`}
                className={cx('editor-word', index === active && 'is-active')}
                role="option"
                aria-selected={index === active}
                onMouseDown={(event) => {
                  // Keeps the caret, and so the list, in the draft.
                  event.preventDefault();
                }}
                onClick={() => {
                  complete(word);
                }}
              >
                {list.typo ? (
                  word
                ) : (
                  <>
                    <span className="editor-word-typed">
                      {word.slice(0, list.end - list.start)}
                    </span>
                    {word.slice(list.end - list.start)}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {fixing && (
          <ul
            ref={listRef}
            id={`${id}-fixes`}
            className="editor-word-list"
            role="listbox"
            aria-label={t.editor.spelling}
          >
            {[...fixing.fixes, null].map((fix, index) => (
              <li
                key={fix ?? ''}
                id={`${id}-fix-${String(index)}`}
                className={cx(
                  'editor-word',
                  fix === null && 'editor-word--add',
                  index === fixIndex && 'is-active',
                )}
                role="option"
                aria-selected={index === fixIndex}
                onMouseDown={(event) => {
                  // Keeps the caret, and so the suggestions, in the draft.
                  event.preventDefault();
                }}
                onClick={() => {
                  takeFix(index);
                }}
              >
                {fix ?? t.editor.addToDictionary}
              </li>
            ))}
          </ul>
        )}
        <p className="sr-only" aria-live="polite">
          {announced && t.editor.suggestion(announced)}
        </p>
        {/* Where what's dragged over the page goes, wherever it's dropped. */}
        {dropping && !readOnly && (
          <div
            className={cx('editor-drop', dropping === 'draft' && 'is-over-draft')}
            aria-hidden="true"
          >
            <span className="editor-drop-label">
              <Icon name="upload" />
              {t.editor.dropHere}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * What a line Enter just began is written in: the formatting where the break was, while the line
 * has no words of its own, which keep theirs. Code doesn't carry on, nor a link.
 */
function carriedOn(formats: Formats, edit: Edit): Pending | null {
  const line = lineAt(parseMarkdown(edit.text), edit.selection[0]);
  if (line.kind === 'code' || line.kind === 'fence' || edit.text.slice(line.from, line.end))
    return null;
  const carried: { -readonly [K in keyof Pending]: Pending[K] } = {};
  for (const kind of formats.marks) if (kind !== 'code') carried[kind] = true;
  if (formats.color) carried.color = formats.color;
  if (formats.highlight) carried.highlight = formats.highlight;
  return Object.keys(carried).length > 0 ? carried : null;
}

/** The range the browser says an input applies to, as offsets in the draft. */
function targetRange(element: HTMLElement, event: InputEvent): Selection | null {
  const [range] = typeof event.getTargetRanges === 'function' ? event.getTargetRanges() : [];
  if (!range || !element.contains(range.startContainer) || !element.contains(range.endContainer))
    return null;
  const start = offsetAt(element, range.startContainer, range.startOffset);
  const end = offsetAt(element, range.endContainer, range.endOffset);
  return start <= end ? [start, end] : [end, start];
}

/** What a word or line deletion takes, where the browser doesn't say. */
function wordRange(text: string, start: number, end: number, type: string): Selection {
  if (start !== end) return [start, end];
  const backward = type.endsWith('Backward');
  if (type.includes('Line')) {
    if (backward) return [text.lastIndexOf('\n', start - 1) + 1, start];
    const next = text.indexOf('\n', start);
    return [start, next === -1 ? text.length : next];
  }
  if (backward) {
    let from = start;
    while (from > 0 && /\s/u.test(text[from - 1] ?? '')) from -= 1;
    const word = wordAt(text, from > 0 ? from - 1 : from);
    from = word ? word[0] : Math.max(0, from - 1);
    return [from, start];
  }
  let to = start;
  while (to < text.length && /\s/u.test(text[to] ?? '')) to += 1;
  const word = wordAt(text, to + 1);
  return [start, word ? word[1] : Math.min(text.length, to + 1)];
}

/** Whether a paste or a drop brings writing: plain, formatted, or the draft's own. */
function carriesText(data: DataTransfer | null): boolean {
  return data?.types.some((type) => type.startsWith('text/')) === true;
}

/**
 * Whether what's pasted or dropped on `target`, outside the draft, is for the draft: not when it
 * goes into another field, or a menu or a dialog is where it's done.
 */
function fromPage(field: HTMLElement, target: EventTarget | null): boolean {
  if (field.ownerDocument.querySelector('dialog[open]')) return false;
  if (!(target instanceof Element)) return true;
  return (
    !field.contains(target) &&
    target.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="dialog"]',
    ) === null
  );
}

/** The offset in the draft at a point on the screen, as where something is dropped. */
function pointFrom(element: HTMLElement, x: number, y: number): number | null {
  const document = element.ownerDocument as Partial<Pick<Document, 'caretPositionFromPoint'>>;
  const position = document.caretPositionFromPoint?.(x, y);
  return position && element.contains(position.offsetNode)
    ? offsetAt(element, position.offsetNode, position.offset)
    : null;
}
